
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BYPASS_COOKIE = 'maint_bypass';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export default function MaintenanceGate({ children }) {
  const location = useLocation();
  const { booted, user, role } = useAuth();

  const [hasBypass, setHasBypass] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [checked, setChecked] = useState(false);

  const roleAllowed = useMemo(() => {
    return role === 'admin' || role === 'owner' || role === 'developer';
  }, [role]);

  const isMaintenanceRoute = location.pathname.startsWith('/maintenance');

  useEffect(() => {
    setHasBypass(!!getCookie(BYPASS_COOKIE));
  }, [location.pathname, location.search]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const res = await fetch('/api/maintenance/status', {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!alive) return;

        if (res.ok) {
          const data = await res.json();
          setMaintenanceEnabled(!!data.enabled);
        } else {
          setMaintenanceEnabled(false);
        }
      } catch {
        if (alive) setMaintenanceEnabled(false);
      } finally {
        if (alive) setChecked(true);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [location.pathname, location.search]);

  if (!booted || !checked) return null;

  if (!maintenanceEnabled) {
    if (isMaintenanceRoute) {
      const requestedPath = new URLSearchParams(location.search).get('from');
      const safePath =
        requestedPath &&
        requestedPath.startsWith('/') &&
        !requestedPath.startsWith('//') &&
        !requestedPath.startsWith('/maintenance')
          ? requestedPath
          : '/';

      return <Navigate to={safePath} replace />;
    }

    return children;
  }

  if (!hasBypass) {
    if (isMaintenanceRoute) return children;
    return <Navigate to="/maintenance" replace />;
  }

  if (!user) {
    if (!isMaintenanceRoute) {
      return (
        <Navigate
          to={`/maintenance?login=1&from=${encodeURIComponent(
            location.pathname + location.search
          )}`}
          replace
        />
      );
    }
    return children;
  }

  if (roleAllowed) return children;

  if (isMaintenanceRoute) return children;
  return <Navigate to="/maintenance" replace />;
}
